import { useId, useState } from 'react';
import { AdminSelect } from '../../components/primitives/admin-select.js';
import { useDebouncedValue } from '../../lib/use-debounced-value.js';
import { useAdminMediaLibrary } from '../../media/hooks/use-media.js';
import type { AdminCategory } from '../types/catalog.js';
import type { CreateCategoryInput } from '../api/catalog-api.js';

export interface CategoryFormProps {
  categories: AdminCategory[];
  initial?: AdminCategory;
  onSubmit: (input: CreateCategoryInput) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

/**
 * Single-image "pick from media library" widget, following the same
 * search + thumbnail-grid pattern as the product media tab
 * (product-media-tab.tsx) and the marketing MediaAssetPicker, but scoped
 * as a local component (rather than reusing MediaAssetPicker directly) so
 * two instances -- image and banner -- can appear in the same form without
 * colliding on a shared, non-unique DOM id for the fallback text input.
 */
function CategoryImagePicker({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (mediaAssetId: string) => void;
}) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const { data, isLoading } = useAdminMediaLibrary({
    search: debouncedSearch || undefined,
    kind: 'image',
    limit: 24,
  });
  const assets = data ?? [];
  const inputId = useId();

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-gray-600">{label}</label>
      <input
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search media library…"
        className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
      />
      <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-100 p-2">
        {isLoading && <p className="p-2 text-sm text-gray-400">Loading…</p>}
        {!isLoading && assets.length === 0 && <p className="p-2 text-sm text-gray-400">No media found.</p>}
        {!isLoading && assets.length > 0 && (
          <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
            {assets.map((asset) => (
              <button
                key={asset.id}
                type="button"
                onClick={() => onChange(asset.id)}
                aria-pressed={value === asset.id}
                title={asset.id}
                className={`focus-visible:ring-brand aspect-square overflow-hidden rounded-lg border-2 focus-visible:outline-none focus-visible:ring-2 ${
                  value === asset.id ? 'border-gray-900' : 'border-transparent hover:border-gray-200'
                }`}
              >
                {asset.url ? (
                  <img src={asset.url} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-gray-100 text-[10px] text-gray-400">
                    No preview
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      <div>
        <label htmlFor={inputId} className="mb-1 block text-xs font-medium text-gray-600">
          {label} asset ID
        </label>
        <input
          id={inputId}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="00000000-0000-0000-0000-000000000000"
          className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 font-mono text-xs focus-visible:outline-none focus-visible:ring-2"
        />
      </div>
    </div>
  );
}

export function CategoryForm({ categories, initial, onSubmit, onCancel, isSubmitting }: CategoryFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [parentId, setParentId] = useState(initial?.parentId ?? '');
  const [sortOrder, setSortOrder] = useState(String(initial?.sortOrder ?? 0));
  const [imageMediaAssetId, setImageMediaAssetId] = useState(initial?.imageMediaAssetId ?? '');
  const [bannerMediaAssetId, setBannerMediaAssetId] = useState(initial?.bannerMediaAssetId ?? '');
  const [isFeatured, setIsFeatured] = useState(initial?.isFeatured ?? false);
  const [seoTitle, setSeoTitle] = useState(initial?.seoTitle ?? '');
  const [seoMetaDescription, setSeoMetaDescription] = useState(initial?.seoMetaDescription ?? '');
  const [error, setError] = useState<string | null>(null);
  const nameId = useId();
  const seoTitleId = useId();
  const seoMetaDescriptionId = useId();
  const isFeaturedId = useId();

  // A category can never be its own parent -- excluded from the option
  // list entirely rather than merely rejected on submit, so the invalid
  // choice isn't selectable in the first place (deeper circular-hierarchy
  // prevention beyond direct self-parenting is still enforced
  // authoritatively server-side; see docs/catalog-architecture.md).
  const parentOptions = categories.filter((c) => c.id !== initial?.id);

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    setError(null);
    onSubmit({
      name: name.trim(),
      parentId: parentId || null,
      sortOrder: Number(sortOrder) || 0,
      imageMediaAssetId: imageMediaAssetId.trim() || null,
      bannerMediaAssetId: bannerMediaAssetId.trim() || null,
      isFeatured,
      seoTitle: seoTitle.trim() || null,
      seoMetaDescription: seoMetaDescription.trim() || null,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-xl border border-gray-100 bg-gray-50 p-4">
      <div>
        <label htmlFor={nameId} className="mb-1 block text-xs font-medium text-gray-600">
          Name
        </label>
        <input
          id={nameId}
          value={name}
          onChange={(event) => setName(event.target.value)}
          className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <AdminSelect
          label="Parent category"
          value={parentId}
          onChange={setParentId}
          allLabel="Root category (no parent)"
          options={parentOptions.map((c) => ({ value: c.id, label: c.name }))}
        />
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">Sort order</label>
          <input
            inputMode="numeric"
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value)}
            className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <CategoryImagePicker label="Category image" value={imageMediaAssetId} onChange={setImageMediaAssetId} />
        <CategoryImagePicker label="Category banner" value={bannerMediaAssetId} onChange={setBannerMediaAssetId} />
      </div>

      <div className="flex items-center gap-2">
        <input
          id={isFeaturedId}
          type="checkbox"
          checked={isFeatured}
          onChange={(event) => setIsFeatured(event.target.checked)}
          className="focus-visible:ring-brand h-4 w-4 rounded border-gray-300 focus-visible:outline-none focus-visible:ring-2"
        />
        <label htmlFor={isFeaturedId} className="text-sm text-gray-700">
          Featured category
        </label>
      </div>

      <div>
        <label htmlFor={seoTitleId} className="mb-1 block text-xs font-medium text-gray-600">
          SEO title
        </label>
        <input
          id={seoTitleId}
          value={seoTitle}
          onChange={(event) => setSeoTitle(event.target.value)}
          className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
        />
      </div>

      <div>
        <label htmlFor={seoMetaDescriptionId} className="mb-1 block text-xs font-medium text-gray-600">
          SEO meta description
        </label>
        <textarea
          id={seoMetaDescriptionId}
          value={seoMetaDescription}
          onChange={(event) => setSeoMetaDescription(event.target.value)}
          rows={3}
          className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
        />
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="bg-brand text-brand-foreground rounded-full px-4 py-1.5 text-sm font-semibold disabled:opacity-60"
        >
          {isSubmitting ? 'Saving…' : 'Save category'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-full border border-gray-200 px-4 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
